import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  ConsultantPortalContract,
  ConsultantPortalContractStatus,
} from '@alamo/types';

import { apiClient } from '../client';

const portalContractKey = (userId: string) => ['consultant-contract', 'portal', userId] as const;

export function usePortalConsultantContract(userId: string, enabled = true) {
  return useQuery({
    queryKey: portalContractKey(userId),
    enabled: Boolean(userId) && enabled,
    queryFn: async () =>
      (
        await apiClient.get<ApiSuccess<ConsultantPortalContractStatus>>(
          '/consultant-contract/status',
        )
      ).data.data,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) => (query.state.data?.isBlocked ? 5000 : 30000),
  });
}

export function useInitiatePortalConsultantContract(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<ApiSuccess<ConsultantPortalContract>>('/consultant-contract/initiate'))
        .data.data,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: portalContractKey(userId) });
    },
  });
}

export function useCompletePortalConsultantContract() {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<ApiSuccess<{ completed: boolean }>>('/consultant-contract/complete'))
        .data.data,
  });
}
